// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useMemo, useState } from 'react';
import { IconButton, TextField, InputAdornment, Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import LayersIcon from '@mui/icons-material/Layers';
import SVG from 'react-inlinesvg';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { RootState } from 'Store';
import { getOrders } from 'Features/order/orderSelectors';
import { SetShowSidebarAction } from 'Features/map/mapEditorActions';
import sideBarIcon from 'Assets/sidebar-icon.svg';
import stylesDef from './styles';

interface LayersPanelProps {
  orderId: string;
  onBack: () => void;
}

const LayersPanel = ({ orderId, onBack }: LayersPanelProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const [search, setSearch] = useState('');

  const { activeOrder, projectOrdersReverse } = useAppSelector((state: RootState) => getOrders(state));
  const order = projectOrdersReverse.find((o: IOrder) => o._id === orderId) || activeOrder;
  const isActive = order?._id === activeOrder?._id;

  const bundleDeliverables = useAppSelector((state) => state.bundle.bundleDeliverables);
  const selectedAdditionals = useAppSelector((state) => state.bundle.selectedAdditionals);

  const layers = useMemo(() => {
    if (!order) return [];
    const orderDeliverables = order.deliverables || [];
    const orderAddOns = order.addOns || [];
    const hasOrderLevelData = orderDeliverables.length > 0 || orderAddOns.length > 0;

    const items: { name: string; type: string }[] = [];
    if (hasOrderLevelData) {
      orderDeliverables.forEach((d) => items.push({ name: d.name, type: d.addOnType || d.type || '' }));
      orderAddOns.forEach((d) => items.push({ name: d.name, type: d.addOnType || d.type || '' }));
    } else if (isActive && order.isEstimate) {
      bundleDeliverables?.forEach((d) => items.push({ name: d.name, type: d.type }));
      selectedAdditionals?.forEach((d) => items.push({ name: d.name, type: d.type }));
    }
    return items;
  }, [order, isActive, bundleDeliverables, selectedAdditionals]);

  const filteredLayers = useMemo(() => {
    if (!search.trim()) return layers;
    const term = search.toLowerCase();
    return layers.filter((l) => l.name.toLowerCase().includes(term));
  }, [layers, search]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={styles.title}>Layers</Typography>
        </Box>
        <IconButton size="small" onClick={() => dispatch(SetShowSidebarAction(false))}>
          <SVG src={sideBarIcon} style={styles.sidebarIcon} />
        </IconButton>
      </Box>
      <Box sx={styles.searchWrapper}>
        <TextField
          fullWidth
          placeholder="Search Layers"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={styles.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={styles.list}>
        {filteredLayers.length === 0 && (
          <Typography sx={styles.emptyText}>No layers found.</Typography>
        )}
        {filteredLayers.map((layer) => (
          <Box key={layer.name} sx={styles.layerItem}>
            <LayersIcon sx={styles.layerIcon} />
            <Typography sx={styles.layerName}>{layer.name}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LayersPanel;
